import { formBrief, withFormat, type Brief } from '../lib/brief'
import { customTopic, retryTopic, type TopicRequest } from '../lib/sections'
import { ApiError } from '../api/client'

export const SUBJECT_MAX = 200
export const ASK_MAX = 500
export const NOTES_MAX = 1000

/** A subject Start was opened with (a ranked idea's full topic, or a piece's stored one) and the
 *  form values it filled in. The topic is sent whole; only a field the editor changed overrides it. */
export type StartBase = { topic: TopicRequest; subject: string; ask: string; pieceType: string }

export type StartState = {
  subject: string
  /** "What should it answer?" (the topic's search_intent). */
  ask: string
  pieceType: string
  brief: Brief
  /** Check-first follows the format until the editor ticks or clears it. */
  touchedReview: boolean
  /** Null for the editor's own subject (?topic=&ask=). */
  base: StartBase | null
}

export type StartBody = { topic: TopicRequest; brief: Brief }

const FALLBACK: Pick<Brief, 'format' | 'sources' | 'guidance' | 'destination'> = {
  format: 'long', sources: 'both', guidance: '', destination: 'section',
}

function baseFrom(topic: TopicRequest, askFallback = ''): StartBase {
  return {
    topic,
    subject: topic.label.trim().slice(0, SUBJECT_MAX),
    ask: (topic.search_intent || askFallback).trim().slice(0, ASK_MAX),
    pieceType: topic.piece_type ?? '',
  }
}

/** Where the form begins: a piece being started again wins over the editor's saved defaults,
 *  which win over the built-in ones. A ranked idea (?idea=, found and passed in as `idea`) fills the
 *  subject from its full topic; ?topic= and ?ask= fill it for the editor's own idea, and ?idea= alone
 *  (an idea no longer ranked) fills just the name. */
export function initialStart(
  params: URLSearchParams,
  defaults?: Partial<Brief> | null,
  fromDraft?: { topic?: { label?: string; search_intent?: string; piece_type?: string | null } | null; brief?: Brief | null } | null,
  idea?: TopicRequest | null,
): StartState {
  if (fromDraft) {
    const t = retryTopic(fromDraft.topic)
    const base = t ? baseFrom(t) : null
    const brief = formBrief({ ...FALLBACK, ...(fromDraft.brief ?? {}) })
    return {
      subject: base?.subject ?? (params.get('topic') ?? '').trim().slice(0, SUBJECT_MAX),
      ask: base?.ask ?? (params.get('ask') ?? '').trim().slice(0, ASK_MAX),
      pieceType: base?.pieceType ?? '',
      brief,
      touchedReview: true,
      base,
    }
  }
  const d = defaults ?? {}
  const base = formBrief({
    format: d.format ?? FALLBACK.format,
    sources: d.sources ?? FALLBACK.sources,
    guidance: '',
    destination: d.destination ?? FALLBACK.destination,
  })
  const ideaBase = idea?.label?.trim() ? baseFrom(idea, idea.representative_query ?? '') : null
  return {
    subject: ideaBase?.subject ?? (params.get('topic') ?? params.get('idea') ?? '').trim().slice(0, SUBJECT_MAX),
    ask: ideaBase?.ask ?? (params.get('ask') ?? '').trim().slice(0, ASK_MAX),
    pieceType: ideaBase?.pieceType ?? '',
    brief: { ...base, review_sources: base.format === 'deep' },
    touchedReview: false,
    base: ideaBase,
  }
}

export const withStartFormat = (s: StartState, format: Brief['format']): StartState =>
  ({ ...s, brief: withFormat(s.brief, format, s.touchedReview) })

/** What draft-now takes. The brief is exactly what the old form sent. A subject Start was opened
 *  with goes whole (its search data, type and signals brief the writer), with only what the editor
 *  changed laid over it; the editor's own subject is built from the form. */
export function startBody(s: StartState, typed = true): StartBody {
  const brief = formBrief(s.brief)
  if (s.base) {
    const topic: TopicRequest = { ...s.base.topic }
    const subject = s.subject.trim()
    const ask = s.ask.trim()
    if (subject !== s.base.subject) topic.label = subject
    if (ask !== s.base.ask) topic.search_intent = ask
    if (s.pieceType !== s.base.pieceType) topic.piece_type = s.pieceType || null
    return { topic, brief }
  }
  const topic = customTopic(s.subject, s.ask, typed && s.pieceType ? s.pieceType : null)
  return { topic: topic ?? { label: '' }, brief }
}

export const ALREADY_WRITING = 'A piece is already being written for this title. Try again when it finishes.'

/** draft-now and /write answer 202 even when they refuse: `started: false, already_running: true`.
 *  The refusal in words, or null when the piece really started. */
export function startRefusal(res: { started?: boolean; already_running?: boolean } | null | undefined): string | null {
  return res && (res.already_running || res.started === false) ? ALREADY_WRITING : null
}

/** Why Write it waits while the title's writer is busy, or null. */
export const runningBlock = (status: { running?: boolean } | null | undefined): string | null =>
  (status?.running ? ALREADY_WRITING : null)

/** Where Cancel goes: back (-1) when this tab has in-app history, else the Desk. */
export const cancelTarget = (locationKey: string, desk: string): -1 | string => (locationKey !== 'default' ? -1 : desk)

export const primaryLabel = (s: StartState): string => (s.brief.review_sources ? 'Gather sources' : 'Write it')

export function estimateMinutes(format: Brief['format']): number {
  return format === 'deep' ? 15 : format === 'long' ? 6 : 3
}

/** Why Write it is unavailable, or null when it is not. */
export function submitBlock(s: StartState): string | null {
  if (!s.subject.trim()) return 'Add a subject first'
  if (s.subject.length > SUBJECT_MAX) return `The subject is limited to ${SUBJECT_MAX} characters`
  if (s.ask.length > ASK_MAX) return `The question is limited to ${ASK_MAX} characters`
  if (s.brief.guidance.length > NOTES_MAX) return `Notes are limited to ${NOTES_MAX} characters`
  return null
}
export const canSubmit = (s: StartState): boolean => submitBlock(s) === null

export const IDEA_GONE = 'That idea is no longer in the list, so only its name is filled in.'
export const IDEA_FAILED = 'Couldn’t load this idea — check your connection and try again.'

/** Start opened from an idea (?idea=): what became of it. 'gone' only when the list really came
 *  back without it (or answered 404/410); any other failure (offline, a 500) is 'failed', which
 *  offers Retry instead of saying the idea went away. */
export function ideaLoad(q: { asked: boolean; isError: boolean; error: unknown; found: boolean }): 'none' | 'found' | 'gone' | 'failed' {
  if (!q.asked) return 'none'
  if (q.found) return 'found'
  if (q.isError) return q.error instanceof ApiError && (q.error.status === 404 || q.error.status === 410) ? 'gone' : 'failed'
  return 'gone'
}
